import { Button, Col, Form, Row, Flex } from "antd"
import { compressFile } from "helpers/compress"
import useFormErrors from "hooks/useAntdFormErrors"
import { useDevice } from "hooks/useDevice"
import { serialize } from "object-to-formdata"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { guideSelector, updateGuide, updateUser } from "store/user"
import { CustomForm } from "ui/Form"
import { CustomInput, TextArea } from "ui/Input"
import GooglePlacesNewInput from "modules/google/GooglePlacesNewInput"
import { LocationInfo } from "pagesV2/pro/Experiences/ListingLocation/LocationForm"
import { convertGoogleAddressComponentsToObj } from "modules/google/helpers"
import { ChangeEmailPhoneForm } from "../LoginAndSecurity/ChangeEmailPhoneForm"
import { AddAvatar } from "ui/PersonalInfo/ProfileInformation/AddAvatar"
import { notification } from "ui/Notification"
import styles from "./ProfileInformation.module.css"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { colors } from "themes/variables"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { useAiTextDescription } from "hooks/useAiTextDescription"
export const ProfileInformation = () => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const [manualAddressForm] = Form.useForm()
  const [isFieldsTouched, setIsFieldsTouched] = useState(false)
  const { descriptionValue, setDescriptionValue, isShowAiModal, setIsShowAiModal, profileDescription, setProfileDescription } =
    useAiTextDescription()
  const {
    id,
    display_name,
    company_name,
    first_name,
    last_name,
    email,
    website,
    description,
    country,
    street_address,
    city,
    state,
    qr_code,
    zip_code,
    radius,
    address_line_2
  } = useSelector(guideSelector)
  const {
    avatar,
    guide: { active }
  } = useSelector((store) => store.user)
  const { loading } = useSelector((state) => state.auth)
  const { isDesktop } = useDevice()
  const navigate = useNavigate()

  const [addressText, setAddressText] = useState(null)
  const [manualAddress, setManualAddress] = useState(false)
  const [centerLatLng, setCenterLatLng] = useState(null)

  const requiredErrorMessage = t("form_validation.cannot_be_blank")

  // Initialize address UI based on existing values
  useEffect(() => {
    if (street_address || city || state || zip_code || country) {
      setManualAddress(true)
      const addressStr = [street_address, city, state, zip_code, country].filter(Boolean).join(", ")
      if (addressStr) setAddressText(addressStr)
    }
  }, [street_address, city, state, zip_code, country])

  useEffect(() => {
    if (!manualAddress && address_line_2) {
      setAddressText(address_line_2)
      form.setFieldsValue({ address_line_2 })
    }
  }, [manualAddress, address_line_2, form])

  // Prefill manual address form when entering manual mode or when redux values change
  useEffect(() => {
    if (!manualAddress) return
    manualAddressForm.setFieldsValue({
      street_address: street_address || "",
      city: city || "",
      state: state || "",
      zip_code: zip_code || "",
      country: country || undefined
    })
  }, [manualAddress, street_address, city, state, zip_code, country, manualAddressForm])

  const placeChangedHandler = ({ target }) => {
    const { place, value, coordinates } = target || {}
    if (place?.address_components) {
      const config = {
        street_address: { elements: ["street_number", "route"], divider: " " },
        city: { elements: ["locality"] },
        state: { elements: ["administrative_area_level_1"] },
        zip_code: { elements: ["postal_code", "postal_code_suffix"], divider: "-" },
        country: { elements: ["country"] }
      }
      const obj = convertGoogleAddressComponentsToObj(place.address_components, config)
      form.setFieldsValue(obj)
      setIsFieldsTouched(true)
    }
    if (typeof coordinates?.lat === "number" && typeof coordinates?.lng === "number") {
      setCenterLatLng({ lat: coordinates.lat, lng: coordinates.lng })
    }
    if (value !== undefined) {
      setAddressText(value)
      // keep form in sync so Form.Item required rule can validate correctly
      form.setFieldsValue({ address_line_2: value })
      // trigger validation and enable Save button
      form.validateFields(["address_line_2"])
      setIsFieldsTouched(true)
    }
  }

  const onFinish = (formData) => {
    const fields = ["street_address", "city", "state", "zip_code", "country"]
    const manualVals = manualAddressForm.getFieldsValue(fields) || {}
    const addressLine2 = formData?.address_line_2 ?? ""
    const addressPayload = fields.reduce((acc, key) => {
      acc[key] = manualVals?.[key] ?? formData?.[key] ?? ""
      return acc
    }, {})
    const newFormData = {
      ...formData,
      longitude: centerLatLng?.lng,
      latitude: centerLatLng?.lat,
      ...addressPayload,
      address_line_2: addressLine2,
      is_manual_address: Boolean(manualAddress)
    }
    const serializedFormData = serialize({ guide: newFormData }, { indices: true })
    dispatch(updateGuide(id, serializedFormData))
      .then(() => {
        setIsFieldsTouched(false)
        notification.success({
          message: t("user.guide_success_update")
        })
      })
      .catch(() => {
        notification.error({
          message: t("user.guide_update_error")
        })
      })
  }

  const handleFieldsChange = () => {
    setIsFieldsTouched(true)
  }

  const handleNavigateBack = () => {
    navigate(-1)
  }

  const handleCancel = () => {
    handleNavigateBack()
  }

  // Called when manual address is confirmed successfully inside LocationInfo
  const handleManualAddressConfirmed = () => {
    // Mark main form as dirty so Save Changes becomes enabled
    setIsFieldsTouched(true)
    // Optionally sync manual address fields into the main form values so they are included on save
    try {
      const addrValues = manualAddressForm.getFieldsValue(["street_address", "city", "state", "zip_code", "country"])
      if (addrValues) form.setFieldsValue(addrValues)
    } catch (e) {
      console.error(e)
    }
  }

  const handleImageChange = async (e) => {
    const avatar = await compressFile(e.target.files[0])
    const formData = serialize({ avatar })
    if (avatar) dispatch(updateUser(formData))
  }
  const downloadQR = () => {
    fetch(qr_code, {
      method: "GET",
      headers: {}
    })
      .then((response) => {
        response.arrayBuffer().then(function (buffer) {
          const url = window.URL.createObjectURL(new Blob([buffer]))
          const link = document.createElement("a")
          link.href = url
          link.setAttribute("download", "qr_code.png")
          document.body.appendChild(link)
          link.click()
        })
      })
      .catch((err) => {
        console.log(err)
      })
  }
  useFormErrors(form)

  useEffect(() => {
    form.setFieldsValue({ description: descriptionValue })
  }, [descriptionValue, form.setFieldsValue])

  useEffect(() => {
    if (description) {
      setDescriptionValue(description)
    }
  }, [description])

  useEffect(() => {
    setIsFieldsTouched(descriptionValue !== description)
  }, [descriptionValue, description])

  const descriptionLength = descriptionValue.length < 25

  return (
    <>
      <CustomForm layout="vertical">
        <Form.Item name="avatarPhoto" label={t("guide.profile_image")} required={true} className="mb-4xl  relative w-100 ">
          <AddAvatar avatar={avatar} handleImageChange={handleImageChange} />
          {qr_code && (
            <Col xs={4} sm={4} className="text-center absolute top-20 right-0 avtarWrapper">
              <img src={qr_code} className="w-100" alt="" />
              <Button className="mx-auto" color="primary-second" outline onClick={downloadQR}>
                Download
              </Button>
            </Col>
          )}
        </Form.Item>
      </CustomForm>
      <CustomForm
        form={form}
        name="settings-profile-information"
        layout="vertical"
        initialValues={{
          display_name,
          company_name,
          email,
          website,
          description,
          user_attributes: {
            first_name,
            last_name
          },
          radius
        }}
        onFinish={onFinish}
        onValuesChange={() => setIsFieldsTouched(true)}
      >
        <Row gutter={12}>
          <Col md={12} sm={24} xs={24}>
            <Form.Item
              label={t("guide.display_name")}
              name="display_name"
              required={true}
              rules={[{ required: true, message: requiredErrorMessage }]}
            >
              <CustomInput placeholder={t("guide.display_name")} onChange={handleFieldsChange} />
            </Form.Item>
          </Col>
          <Col md={12} sm={24} xs={24}>
            <Form.Item label={t("guide.company_name")} name="company_name">
              <CustomInput placeholder={t("guide.company_name")} onChange={handleFieldsChange} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col md={12} sm={24} xs={24}>
            <Form.Item
              label={t("sign_up.first_name")}
              name={["user_attributes", "first_name"]}
              required={true}
              rules={[{ required: true, message: requiredErrorMessage }]}
            >
              <CustomInput placeholder={t("sign_up.first_name")} onChange={handleFieldsChange} />
            </Form.Item>
          </Col>
          <Col md={12} sm={24} xs={24}>
            <Form.Item
              label={t("sign_up.last_name")}
              name={["user_attributes", "last_name"]}
              required={true}
              rules={[{ required: true, message: requiredErrorMessage }]}
            >
              <CustomInput placeholder={t("sign_up.last_name")} onChange={handleFieldsChange} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col md={12} sm={24} xs={24}>
            <Form.Item
              label={t("user.email")}
              name="email"
              required={true}
              rules={[
                { required: true, message: requiredErrorMessage },
                { type: "email", message: t("form_validation.invalid_email") }
              ]}
            >
              <CustomInput placeholder={t("user.email")} onChange={handleFieldsChange} disabled />
            </Form.Item>
          </Col>
          <Col md={12} sm={24} xs={24}>
            <ChangeEmailPhoneForm inputType="phone_number" />
          </Col>
        </Row>

        <Form.Item label={t("guide.website")} name="website">
          <CustomInput placeholder="https://" onChange={handleFieldsChange} />
        </Form.Item>
        <Form.Item name="description" required={true} rules={[{ required: true, message: t("pro.ai_enhancement.description_too_short") }]}>
          <Flex justify="space-between" align="center" className="mb-s w-full">
            <Text className={styles.descriptionLabel}>{t("guide.description")}*</Text>
            <Button
              color="primary"
              variant="solid"
              icon={<Icon name="aiEnhancement" color={descriptionLength ? colors.textPrimary : colors.disabled} />}
              disabled={!active || descriptionLength}
              onClick={active && !descriptionLength ? () => setIsShowAiModal(true) : undefined}
            >
              {t("pro.ai_enhancement.button_text")}
            </Button>
          </Flex>
          <TextArea
            placeholder={t("guide.description_placeholder")}
            style={{ height: 147 }}
            onChange={(e) => {
              setDescriptionValue(e.target.value)
            }}
            value={descriptionValue}
          />
        </Form.Item>

        {manualAddress ? (
          <Form.Item>
            <LocationInfo
              isRequired={true}
              screenName="Profile"
              backBtnManualText={t("client.back_to_manual_address")}
              form={manualAddressForm}
              t={t}
              setCenterLatLng={setCenterLatLng}
              setManualAddress={setManualAddress}
              setMeetingLatLng={setAddressText}
              onAddressConfirmed={handleManualAddressConfirmed}
            />
          </Form.Item>
        ) : (
          <Form.Item
            label={t("pro.addresses.street_address")}
            name="address_line_2"
            rules={[{ required: true, message: requiredErrorMessage }]}
          >
            <GooglePlacesNewInput
              placeholder={t("pro.addresses.street_address")}
              onChange={placeChangedHandler}
              meetingLatLng={addressText}
              setManualAddress={setManualAddress}
              setCenterLatLng={setCenterLatLng}
              setMeetingLatLng={setAddressText}
            />
          </Form.Item>
        )}

        <Form.Item label={t("pro.addresses.radius")} name="radius">
          <CustomInput placeholder={t("pro.addresses.radius")} onChange={handleFieldsChange} />
        </Form.Item>

        <div className="flex gap-s mt-4xl mb-4xl">
          <Button
            variant="outlined"
            color="primary"
            size="large"
            style={{ width: isDesktop ? "auto" : "50%" }}
            onClick={active ? handleCancel : undefined}
            disabled={!active}
          >
            {t("global.cancel")}
          </Button>
          <Button
            className="mb-xs"
            type="primary"
            size="large"
            htmlType={active ? "submit" : undefined}
            style={{ width: isDesktop ? "auto" : "50%" }}
            disabled={!active || loading || !isFieldsTouched}
          >
            {t("settings.update")}
          </Button>
        </div>
      </CustomForm>
      {isShowAiModal && (
        <AiEnhancementModal
          open={isShowAiModal}
          onCancel={() => {
            setIsShowAiModal(false)
            setProfileDescription("")
          }}
          profileDescription={profileDescription}
          description={descriptionValue}
          setDescription={setDescriptionValue}
          setProfileDescription={setProfileDescription}
          onOk={() => {
            setIsShowAiModal(false)
          }}
          text={t("pro.ai_enhancement.form.textarea_label")}
          modalTitle={t("pro.ai_enhancement.modal_title")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("pro.ai_enhancement.form.continue_refinement_details")}
          improveWithAITooltipTitle={t("pro.ai_enhancement.form.ai_info")}
        />
      )}
    </>
  )
}
